import React, { useState, useEffect } from 'react';
import { Card, Button, TextInput, Textarea, Label, Tabs, Spinner } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiKey, HiTemplate, HiSave, HiEye, HiEyeOff } from 'react-icons/hi';
import { systemSettingsStore } from '../stores/systemSettingsStore';
import { authStore } from '../stores/authStore';
import { toastStore } from '../stores/toastStore';
import { useNavigate } from 'react-router-dom';

const SystemSettingsPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [googleAiApiKey, setGoogleAiApiKey] = useState('');
  const [showOpenaiApiKey, setShowOpenaiApiKey] = useState(false);
  const [showGoogleAiApiKey, setShowGoogleAiApiKey] = useState(false);
  const [templates, setTemplates] = useState({
    calling: '',
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is root admin
    if (authStore.user?.tenantId !== 'root') {
      toastStore.showError('このページへのアクセス権限がありません');
      navigate('/dashboard');
      return;
    }

    loadSystemSettings();
  }, []);

  const loadSystemSettings = async () => {
    setIsLoading(true);
    try {
      await systemSettingsStore.loadSettings();
      if (systemSettingsStore.settings) {
        setOpenaiApiKey(systemSettingsStore.settings.openaiApiKey || '');
        setGoogleAiApiKey(systemSettingsStore.settings.googleAiApiKey || '');
        if (systemSettingsStore.settings.defaultMessageTemplates) {
          setTemplates({
            calling: systemSettingsStore.settings.defaultMessageTemplates.calling || '',
          });
        }
      }
    } catch (error) {
      toastStore.showError('システム設定の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveApiKey = async () => {
    try {
      await systemSettingsStore.updateOpenAIKey(openaiApiKey);
      toastStore.showSuccess('OpenAI APIキーを保存しました');
    } catch (error) {
      toastStore.showError('OpenAI APIキーの保存に失敗しました');
    }
  };

  const handleSaveGoogleApiKey = async () => {
    try {
      await systemSettingsStore.updateGoogleAIKey(googleAiApiKey);
      toastStore.showSuccess('Google AI APIキーを保存しました');
    } catch (error) {
      toastStore.showError('Google AI APIキーの保存に失敗しました');
    }
  };

  const handleSaveTemplates = async () => {
    try {
      await systemSettingsStore.updateDefaultTemplates(templates);
      toastStore.showSuccess('デフォルトテンプレートを保存しました');
    } catch (error) {
      toastStore.showError('デフォルトテンプレートの保存に失敗しました');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  if (authStore.user?.tenantId !== 'root') {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">システム設定</h1>
        <p className="text-gray-600">システム全体の設定を管理します（rootテナント専用）</p>
      </div>


      <Tabs>
        <Tabs.Item active title="OpenAI APIキー" icon={HiKey}>
          <Card>
            <div className="space-y-4">
              <div>
                <Label htmlFor="openai-key" value="OpenAI APIキー" />
                <div className="flex gap-2">
                  <TextInput
                    id="openai-key"
                    type={showOpenaiApiKey ? "text" : "password"}
                    value={openaiApiKey}
                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="flex-1"
                  />
                  <Button
                    color="gray"
                    onClick={() => setShowOpenaiApiKey(!showOpenaiApiKey)}
                  >
                    {showOpenaiApiKey ? <HiEyeOff className="w-4 h-4" /> : <HiEye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  画像解析に使用するOpenAIのAPIキー（システム全体で共有）
                </p>
              </div>

              <Button onClick={handleSaveApiKey} disabled={systemSettingsStore.isLoading}>
                <HiSave className="mr-2 h-4 w-4" />
                {systemSettingsStore.isLoading ? '保存中...' : 'APIキーを保存'}
              </Button>
            </div>
          </Card>
        </Tabs.Item>

        <Tabs.Item title="Google AI APIキー" icon={HiKey}>
          <Card>
            <div className="space-y-4">
              <div>
                <Label htmlFor="google-ai-key" value="Google AI APIキー" />
                <div className="flex gap-2">
                  <TextInput
                    id="google-ai-key"
                    type={showGoogleAiApiKey ? "text" : "password"}
                    value={googleAiApiKey}
                    onChange={(e) => setGoogleAiApiKey(e.target.value)}
                    placeholder="AIza..."
                    className="flex-1"
                  />
                  <Button
                    color="gray"
                    onClick={() => setShowGoogleAiApiKey(!showGoogleAiApiKey)}
                  >
                    {showGoogleAiApiKey ? <HiEyeOff className="w-4 h-4" /> : <HiEye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  画像解析に使用するGoogle AIのAPIキー（システム全体で共有）
                </p>
              </div>

              <Button onClick={handleSaveGoogleApiKey} disabled={systemSettingsStore.isLoading}>
                <HiSave className="mr-2 h-4 w-4" />
                {systemSettingsStore.isLoading ? '保存中...' : 'APIキーを保存'}
              </Button>
            </div>
          </Card>
        </Tabs.Item>

        <Tabs.Item title="デフォルト通知テンプレート" icon={HiTemplate}>
          <Card>
            <div className="space-y-4">
              <div>
                <Label htmlFor="calling-template" value="呼び出し通知テンプレート" />
                <Textarea
                  id="calling-template"
                  rows={4}
                  value={templates.calling}
                  onChange={(e) => setTemplates({ ...templates, calling: e.target.value })}
                  helperText="利用可能な変数: {{number}} - 番号, {{location}} - 場所"
                />
              </div>

              <Button onClick={handleSaveTemplates} disabled={systemSettingsStore.isLoading}>
                <HiSave className="mr-2 h-4 w-4" />
                {systemSettingsStore.isLoading ? '保存中...' : 'テンプレートを保存'}
              </Button>
            </div>
          </Card>
        </Tabs.Item>
      </Tabs>
    </div>
  );
});

export default SystemSettingsPage;